import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportDataParseResult, ImportUserRow, ImportSupplierRow, ImportAssetRow, ImportUseContractRow, ImportMaintenanceContractRow, ImportInsuranceContractRow } from 'api/import-data/models/ImportDataParseResult';
import ImportEditableGrid, { ColumnDefinition } from './ImportEditableGrid';
import styles from './ImportPreview.module.scss';

interface Props {
    data: ImportDataParseResult;
    onDataChange: (data: ImportDataParseResult) => void;
    existingExpenseTypes?: string[];
    existingMaintenanceTypes?: string[];
    existingInsuranceTypes?: string[];
    existingSegments?: string[];
    existingBrands?: string[];
    existingRoles?: string[];
    existingLanguages?: string[];
    existingRegistrations?: string[];
}

type SheetType = 'users' | 'suppliers' | 'assets' | 'useContracts' | 'maintenanceContracts' | 'insuranceContracts';

const ImportPreview: React.FC<Props> = ({ data, onDataChange, existingExpenseTypes = [], existingMaintenanceTypes = [], existingInsuranceTypes = [], existingSegments = [], existingBrands = [], existingRoles = [], existingLanguages = [], existingRegistrations = [] }) => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState<SheetType>('users');
    const [customExpenseTypes, setCustomExpenseTypes] = useState<string[]>([]);
    const [customMaintenanceTypes, setCustomMaintenanceTypes] = useState<string[]>([]);
    const [customInsuranceTypes, setCustomInsuranceTypes] = useState<string[]>([]);
    const [customSegments, setCustomSegments] = useState<string[]>([]);
    const [customBrands, setCustomBrands] = useState<string[]>([]);

    const tabs: { id: SheetType; label: string; totalRows: number; errorCount: number }[] = useMemo(() => [
        { id: 'users', label: t('import_data.sheets.users'), totalRows: data.summary.users.totalRows, errorCount: data.summary.users.errorRows },
        { id: 'suppliers', label: t('import_data.sheets.suppliers'), totalRows: data.summary.suppliers.totalRows, errorCount: data.summary.suppliers.errorRows },
        { id: 'assets', label: t('import_data.sheets.assets'), totalRows: data.summary.assets.totalRows, errorCount: data.summary.assets.errorRows },
        { id: 'useContracts', label: t('import_data.sheets.use_contracts'), totalRows: data.summary.useContracts.totalRows, errorCount: data.summary.useContracts.errorRows },
        { id: 'maintenanceContracts', label: t('import_data.sheets.maintenance_contracts'), totalRows: data.summary.maintenanceContracts.totalRows, errorCount: data.summary.maintenanceContracts.errorRows },
        { id: 'insuranceContracts', label: t('import_data.sheets.insurance_contracts'), totalRows: data.summary.insuranceContracts.totalRows, errorCount: data.summary.insuranceContracts.errorRows }
    ], [data.summary, t]);

    // Extract options for dropdown columns from the imported data
    const userNames = useMemo(() =>
        data.users.map(u => u.name).filter(Boolean) as string[],
        [data.users]
    );

    const supplierNames = useMemo(() =>
        data.suppliers.map(s => s.name).filter(Boolean) as string[],
        [data.suppliers]
    );

    const registrations = useMemo(() =>
        data.assets.map(a => a.registration).filter(Boolean) as string[],
        [data.assets]
    );

    // For the multi-select on the Users sheet "Matrículas associadas" column:
    // combine sheet 04 (Assets) registrations with vehicles already in the company DB,
    // plus anything users already have typed so existing values don't disappear.
    const allRegistrations = useMemo(() => {
        const all = new Set<string>();
        registrations.forEach(r => { if (r) all.add(r.trim()); });
        existingRegistrations.forEach(r => { if (r) all.add(r.trim()); });
        data.users.forEach(u => {
            if (u.registrations) {
                u.registrations.split(/[,;]/).forEach(r => {
                    const trimmed = r.trim();
                    if (trimmed) all.add(trimmed);
                });
            }
        });
        return Array.from(all).sort();
    }, [registrations, existingRegistrations, data.users]);

    const contractNumbers = useMemo(() =>
        data.useContracts.map(c => c.contractNumber).filter(Boolean) as string[],
        [data.useContracts]
    );

    // Combine existing expense types with custom ones added by user
    const allExpenseTypes = useMemo(() => {
        const all = new Set([...existingExpenseTypes, ...customExpenseTypes]);
        // Also add types from current supplier data
        data.suppliers.forEach(s => {
            if (s.expenseTypes) {
                s.expenseTypes.split(/[,;]/).forEach(t => {
                    const trimmed = t.trim();
                    if (trimmed) all.add(trimmed);
                });
            }
        });
        return Array.from(all).sort();
    }, [existingExpenseTypes, customExpenseTypes, data.suppliers]);

    // Helper to translate maintenance type enum keys
    const translateMaintenanceType = useCallback((value: string): string => {
        const isSystemType = /^[A-Z_]+$/.test(value);
        if (isSystemType) {
            const translated = t(`common.maintenance_contract_type.${value}` as any);
            return translated !== `common.maintenance_contract_type.${value}` ? translated : value;
        }
        return value;
    }, [t]);

    // Helper to translate insurance type enum keys
    const translateInsuranceType = useCallback((value: string): string => {
        const isSystemType = /^[A-Z_]+$/.test(value);
        if (isSystemType) {
            const translated = t(`common.insurance_contract_types.${value}` as any);
            return translated !== `common.insurance_contract_types.${value}` ? translated : value;
        }
        return value;
    }, [t]);

    // Combine existing maintenance types with custom ones added by user
    const allMaintenanceTypes = useMemo(() => {
        const all = new Set([...existingMaintenanceTypes, ...customMaintenanceTypes]);
        // Also add types from current maintenance contract data (translated)
        data.maintenanceContracts.forEach(c => {
            if (c.type) {
                c.type.split(/[,;]/).forEach(typeValue => {
                    const trimmed = typeValue.trim();
                    if (trimmed) all.add(translateMaintenanceType(trimmed));
                });
            }
        });
        return Array.from(all).sort();
    }, [existingMaintenanceTypes, customMaintenanceTypes, data.maintenanceContracts, translateMaintenanceType]);

    // Combine existing insurance types with custom ones added by user
    const allInsuranceTypes = useMemo(() => {
        const all = new Set([...existingInsuranceTypes, ...customInsuranceTypes]);
        // Also add types from current insurance contract data (translated)
        data.insuranceContracts.forEach(c => {
            if (c.type) {
                c.type.split(/[,;]/).forEach(typeValue => {
                    const trimmed = typeValue.trim();
                    if (trimmed) all.add(translateInsuranceType(trimmed));
                });
            }
        });
        return Array.from(all).sort();
    }, [existingInsuranceTypes, customInsuranceTypes, data.insuranceContracts, translateInsuranceType]);

    // Combine existing segments with custom ones added by user
    const allSegments = useMemo(() => {
        const all = new Set([...existingSegments, ...customSegments]);
        // Also add segments from current asset data
        data.assets.forEach(a => {
            if (a.segment) all.add(a.segment.trim());
        });
        return Array.from(all).sort();
    }, [existingSegments, customSegments, data.assets]);

    // Combine existing brands with custom ones added by user
    const allBrands = useMemo(() => {
        const all = new Set([...existingBrands, ...customBrands]);
        // Also add brands from current asset data
        data.assets.forEach(a => {
            if (a.brand) all.add(a.brand.trim());
        });
        return Array.from(all).sort();
    }, [existingBrands, customBrands, data.assets]);

    // Callback to add new expense type
    const handleAddExpenseType = useCallback((newType: string) => {
        if (newType && !existingExpenseTypes.includes(newType) && !customExpenseTypes.includes(newType)) {
            setCustomExpenseTypes(prev => [...prev, newType]);
        }
    }, [existingExpenseTypes, customExpenseTypes]);

    // Callback to add new maintenance type
    const handleAddMaintenanceType = useCallback((newType: string) => {
        if (newType && !existingMaintenanceTypes.includes(newType) && !customMaintenanceTypes.includes(newType)) {
            setCustomMaintenanceTypes(prev => [...prev, newType]);
        }
    }, [existingMaintenanceTypes, customMaintenanceTypes]);

    // Callback to add new insurance type
    const handleAddInsuranceType = useCallback((newType: string) => {
        if (newType && !existingInsuranceTypes.includes(newType) && !customInsuranceTypes.includes(newType)) {
            setCustomInsuranceTypes(prev => [...prev, newType]);
        }
    }, [existingInsuranceTypes, customInsuranceTypes]);

    // Callback to add new segment
    const handleAddSegment = useCallback((newSegment: string) => {
        if (newSegment && !existingSegments.includes(newSegment) && !customSegments.includes(newSegment)) {
            setCustomSegments(prev => [...prev, newSegment]);
        }
    }, [existingSegments, customSegments]);

    // Callback to add new brand
    const handleAddBrand = useCallback((newBrand: string) => {
        if (newBrand && !existingBrands.includes(newBrand) && !customBrands.includes(newBrand)) {
            setCustomBrands(prev => [...prev, newBrand]);
        }
    }, [existingBrands, customBrands]);

    // Dropdown options for asset fields
    const conditionOptions = useMemo(() => [
        t('vehicle.conditions.NEW' as any),
        t('vehicle.conditions.USED' as any)
    ], [t]);

    const colorOptions = useMemo(() => {
        const colors = [
            t('vehicle.colors.WHITE' as any),
            t('vehicle.colors.BLACK' as any),
            t('vehicle.colors.SILVER' as any),
            t('vehicle.colors.RED' as any),
            t('vehicle.colors.YELLOW' as any),
            t('vehicle.colors.BLUE' as any),
            t('vehicle.colors.GREEN' as any),
            t('vehicle.colors.OTHER' as any)
        ];
        // Sort alphabetically to match site behavior
        return colors.sort((a, b) => a.localeCompare(b));
    }, [t]);

    const fuelOptions = useMemo(() => [
        t('vehicle.fuels.GASOLINE' as any),
        t('vehicle.fuels.DIESEL' as any),
        t('vehicle.fuels.ELECTRIC' as any),
        t('vehicle.fuels.GASEOUS_FUEL' as any),
        t('vehicle.fuels.HYBRID' as any)
    ], [t]);

    const yesNoOptions = useMemo(() => [
        t('common.yes' as any),
        t('common.no' as any)
    ], [t]);

    const periodicityOptions = useMemo(() => [
        t('common.periodicities.MONTHLY' as any),
        t('common.periodicities.QUARTERLY' as any),
        t('common.periodicities.SIX_MONTHLY' as any),
        t('common.periodicities.ANNUAL' as any),
        t('common.periodicities.WEEKLY' as any),
        t('common.periodicities.DAILY' as any)
    ], [t]);

    // Detect language mismatch between imported file and current UI language
    const isLanguageMismatch = useMemo(() => {
        if (!data.detectedLanguage) return false;
        const currentLang = i18n.language?.toUpperCase().substring(0, 2);
        const fileLang = data.detectedLanguage.toUpperCase().substring(0, 2);
        return currentLang !== fileLang;
    }, [data.detectedLanguage, i18n.language]);

    const financingTypeOptions = useMemo(() => [
        t('common.use_contract_types.PURCHASE' as any),
        t('common.use_contract_types.CREDIT' as any),
        t('common.use_contract_types.LEASING' as any),
        t('common.use_contract_types.ALD' as any)
    ], [t]);

    // Use company-specific roles passed as prop
    const roleOptions = useMemo(() => existingRoles, [existingRoles]);

    const languageOptions = useMemo(() => existingLanguages, [existingLanguages]);

    const userColumns: ColumnDefinition[] = useMemo(() => [
        { key: 'name', name: t('import_data.columns.name' as any) },
        { key: 'email', name: t('import_data.columns.email' as any) },
        { key: 'contact', name: t('import_data.columns.contact' as any) },
        { key: 'role', name: t('import_data.columns.role' as any), options: roleOptions },
        { key: 'language', name: t('import_data.columns.language' as any), options: languageOptions },
        { key: 'registrations', name: t('import_data.columns.registrations' as any), width: 320, options: allRegistrations, multiSelect: true, allowCustom: false }
    ], [t, roleOptions, languageOptions, allRegistrations]);

    const supplierColumns: ColumnDefinition[] = useMemo(() => [
        { key: 'name', name: t('import_data.columns.name' as any), width: 200 },
        { key: 'expenseTypes', name: t('import_data.columns.expense_types' as any), width: 300, options: allExpenseTypes, multiSelect: true, allowCustom: true }
    ], [t, allExpenseTypes]);

    const assetColumns: ColumnDefinition[] = useMemo(() => [
        { key: 'segment', name: t('import_data.columns.segment' as any), options: allSegments, allowCustom: true },
        { key: 'brand', name: t('import_data.columns.brand' as any), options: allBrands, allowCustom: true },
        { key: 'model', name: t('import_data.columns.model' as any) },
        { key: 'registration', name: t('import_data.columns.registration' as any) },
        { key: 'year', name: t('import_data.columns.year' as any), width: 80 },
        { key: 'month', name: t('import_data.columns.month' as any), width: 80 },
        { key: 'condition', name: t('import_data.columns.condition' as any), options: conditionOptions },
        { key: 'color', name: t('import_data.columns.color' as any), options: colorOptions },
        { key: 'displacement', name: t('import_data.columns.displacement' as any), width: 100 },
        { key: 'availableForReservation', name: t('import_data.columns.available_reservation' as any), options: yesNoOptions },
        { key: 'responsibleUserName', name: t('import_data.columns.responsible_user' as any), options: userNames },
        { key: 'fuelType1', name: t('import_data.columns.fuel_type_1' as any), options: fuelOptions },
        { key: 'averageConsumption1', name: t('import_data.columns.avg_consumption_1' as any), width: 120 },
        { key: 'fuelType2', name: t('import_data.columns.fuel_type_2' as any), options: fuelOptions },
        { key: 'averageConsumption2', name: t('import_data.columns.avg_consumption_2' as any), width: 120 },
        { key: 'fuelCardSupplier', name: t('import_data.columns.fuel_card_supplier' as any), options: supplierNames },
        { key: 'fuelCardNumber', name: t('import_data.columns.fuel_card_number' as any) }
    ], [t, userNames, supplierNames, conditionOptions, colorOptions, fuelOptions, yesNoOptions, allSegments, allBrands]);

    const useContractColumns: ColumnDefinition[] = useMemo(() => [
        { key: 'registration', name: t('import_data.columns.registration' as any), options: registrations },
        { key: 'contractNumber', name: t('import_data.columns.contract_number' as any) },
        { key: 'financingType', name: t('import_data.columns.financing_type' as any), options: financingTypeOptions },
        { key: 'supplier', name: t('import_data.columns.supplier' as any), options: supplierNames },
        { key: 'periodicity', name: t('import_data.columns.periodicity' as any), options: periodicityOptions },
        { key: 'periodicValue', name: t('import_data.columns.periodic_value' as any), width: 120 },
        { key: 'startDate', name: t('import_data.columns.start_date' as any) },
        { key: 'endDate', name: t('import_data.columns.end_date' as any) },
        { key: 'maxKm', name: t('import_data.columns.max_km' as any), width: 100 },
        { key: 'excessKmCost', name: t('import_data.columns.excess_km_cost' as any), width: 120 },
        { key: 'underKmCost', name: t('import_data.columns.under_km_cost' as any), width: 120 },
        { key: 'warrantyYears', name: t('import_data.columns.warranty_years' as any), width: 100 },
        { key: 'warrantyKms', name: t('import_data.columns.warranty_kms' as any), width: 100 },
        { key: 'iucIncluded', name: t('import_data.columns.iuc_included' as any), width: 100, options: yesNoOptions },
        { key: 'maintenanceIncluded', name: t('import_data.columns.maintenance_included' as any), width: 130, options: yesNoOptions },
        { key: 'insuranceIncluded', name: t('import_data.columns.insurance_included' as any), width: 120, options: yesNoOptions }
    ], [t, registrations, supplierNames, financingTypeOptions, periodicityOptions, yesNoOptions]);

    const maintenanceContractColumns: ColumnDefinition[] = useMemo(() => [
        { key: 'registration', name: t('import_data.columns.registration' as any), options: registrations },
        { key: 'linkedContractNumber', name: t('import_data.columns.linked_contract' as any), width: 180, options: contractNumbers },
        { key: 'type', name: t('import_data.columns.type' as any), width: 250, options: allMaintenanceTypes, multiSelect: true, allowCustom: true },
        { key: 'contractNumber', name: t('import_data.columns.contract_number' as any) },
        { key: 'periodicity', name: t('import_data.columns.periodicity' as any), options: periodicityOptions },
        { key: 'periodicValue', name: t('import_data.columns.periodic_value' as any), width: 120 },
        { key: 'startDate', name: t('import_data.columns.start_date' as any) },
        { key: 'endDate', name: t('import_data.columns.end_date' as any) },
        { key: 'supplier', name: t('import_data.columns.supplier' as any), options: supplierNames }
    ], [t, registrations, contractNumbers, supplierNames, periodicityOptions, allMaintenanceTypes]);

    const insuranceContractColumns: ColumnDefinition[] = useMemo(() => [
        { key: 'registration', name: t('import_data.columns.registration' as any), options: registrations },
        { key: 'linkedContractNumber', name: t('import_data.columns.linked_contract' as any), width: 180, options: contractNumbers },
        { key: 'type', name: t('import_data.columns.type' as any), width: 250, options: allInsuranceTypes, multiSelect: true, allowCustom: true },
        { key: 'policyNumber', name: t('import_data.columns.policy_number' as any), width: 140 },
        { key: 'periodicity', name: t('import_data.columns.periodicity' as any), options: periodicityOptions },
        { key: 'periodicValue', name: t('import_data.columns.periodic_value' as any), width: 120 },
        { key: 'startDate', name: t('import_data.columns.start_date' as any) },
        { key: 'endDate', name: t('import_data.columns.end_date' as any) },
        { key: 'supplier', name: t('import_data.columns.supplier' as any), options: supplierNames }
    ], [t, registrations, contractNumbers, supplierNames, periodicityOptions, allInsuranceTypes]);

    const handleUsersChange = (rows: ImportUserRow[]) => {
        onDataChange({ ...data, users: rows });
    };

    const handleSuppliersChange = (rows: ImportSupplierRow[]) => {
        onDataChange({ ...data, suppliers: rows });
    };

    const handleAssetsChange = (rows: ImportAssetRow[]) => {
        onDataChange({ ...data, assets: rows });
    };

    const handleUseContractsChange = (rows: ImportUseContractRow[]) => {
        onDataChange({ ...data, useContracts: rows });
    };

    const handleMaintenanceContractsChange = (rows: ImportMaintenanceContractRow[]) => {
        onDataChange({ ...data, maintenanceContracts: rows });
    };

    const handleInsuranceContractsChange = (rows: ImportInsuranceContractRow[]) => {
        onDataChange({ ...data, insuranceContracts: rows });
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'users':
                return <ImportEditableGrid data={data.users} columns={userColumns} onRowChange={handleUsersChange} />;
            case 'suppliers':
                return <ImportEditableGrid data={data.suppliers} columns={supplierColumns} onRowChange={handleSuppliersChange} onAddOption={handleAddExpenseType} />;
            case 'assets':
                return <ImportEditableGrid data={data.assets} columns={assetColumns} onRowChange={handleAssetsChange} onAddOption={handleAddSegment} />;
            case 'useContracts':
                return <ImportEditableGrid data={data.useContracts} columns={useContractColumns} onRowChange={handleUseContractsChange} />;
            case 'maintenanceContracts':
                return <ImportEditableGrid data={data.maintenanceContracts} columns={maintenanceContractColumns} onRowChange={handleMaintenanceContractsChange} onAddOption={handleAddMaintenanceType} />;
            case 'insuranceContracts':
                return <ImportEditableGrid data={data.insuranceContracts} columns={insuranceContractColumns} onRowChange={handleInsuranceContractsChange} onAddOption={handleAddInsuranceType} />;
            default:
                return null;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.summary}>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>{t('import_data.summary.total_rows')}:</span>
                    <span className={styles.summaryValue}>{data.summary.totalRows}</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>{t('import_data.summary.valid_rows')}:</span>
                    <span className={`${styles.summaryValue} ${styles.success}`}>{data.summary.validRows}</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>{t('import_data.summary.total_errors')}:</span>
                    <span className={`${styles.summaryValue} ${data.summary.totalErrors > 0 ? styles.error : ''}`}>
                        {data.summary.totalErrors}
                    </span>
                </div>
            </div>

            {isLanguageMismatch && (
                <span className={styles.languageWarning}>
                    {t('import_data.language_mismatch_warning')}
                </span>
            )}

            <div className={styles.tabs}>
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''} ${tab.errorCount > 0 ? styles.tabError : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                        <span className={styles.rowCount} title={t('import_data.rows_to_import')}>
                            ({tab.totalRows})
                        </span>
                        {tab.errorCount > 0 && (
                            <span className={styles.errorIcon} title={`${tab.errorCount} ${t('import_data.errors')}`}>
                                <i className="fas fa-exclamation-triangle" />
                            </span>
                        )}
                    </button>
                ))}
            </div>

            <div className={styles.content}>
                {renderContent()}
            </div>
        </div>
    );
};

export default ImportPreview;
